/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { PlaceholderButton } from "../../../components/PlaceholderButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { UseFieldArrayAppend, useFormContext } from "react-hook-form";
import { MenuProductsFormValues } from "../MenuUpsert";
import { ButtonIcon, ButtonPrimary } from "../../../components/NewButtons";
import { MultiSelectModal } from "../../../components/MultiSelectModal";
import { MenuProduct, OverwriteType } from "../models/Menus";
import { OverwriteMenuModalFormValues } from "./OverwriteMenuModal";
import { ProductsFilter } from "../../products/ProductsFilter";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";

type Props = {
  append: UseFieldArrayAppend<any, "products">;
  buttonType?: "placeholder" | "regular";
  categoryId?: string;
  isOverwrite?: boolean;
  availableProducts: MenuProduct[] | undefined;
  availableProductsAreLoading: boolean;
};

export const AddMenuProductsSelector = ({
  append,
  buttonType,
  categoryId,
  isOverwrite,
  availableProducts,
  availableProductsAreLoading,
}: Props) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { watch } = useFormContext<
    MenuProductsFormValues | OverwriteMenuModalFormValues
  >();

  const { categories } = useSearchParamsManipulation();
  const products = watch("products");
  const currentProductIds = products.map((p) => p.id);
  const productOptions = (availableProducts || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((p) => {
      const isCurrentProduct = currentProductIds.includes(p.id);
      if (categoryId) {
        return !isCurrentProduct && p.category.id === categoryId;
      }
      return !isCurrentProduct;
    })
    .filter((p) => {
      if (categories && categories.length > 0) {
        return categories.includes(p.category.name);
      }
      return true;
    })
    .map((p) => ({
      value: p.id,
      label: p.name,
    }));

  return (
    <div>
      {!!categoryId ? (
        <ButtonIcon onClick={() => setModalOpen(true)} type="button">
          <FontAwesomeIcon icon={solid("plus")} />
        </ButtonIcon>
      ) : buttonType === "placeholder" ? (
        <PlaceholderButton
          type="button"
          icon={<FontAwesomeIcon icon={solid("cart-plus")} />}
          header="Click to add the first product to this menu"
          subHeader="No products are currently added to this menu"
          onClick={() => setModalOpen(true)}
        />
      ) : (
        <ButtonPrimary
          type="button"
          onClick={() => setModalOpen(true)}
          icon={<FontAwesomeIcon icon={solid("plus")} />}
        >
          Add product
        </ButtonPrimary>
      )}
      <MultiSelectModal
        options={productOptions}
        header="Add products to menu"
        isLoading={availableProductsAreLoading}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        topComponent={<ProductsFilter />}
        onSave={(addedProducts: string[]) => {
          const productsToAdd = availableProducts?.filter((p) =>
            addedProducts.includes(p.id)
          );
          const indexedProductsToAdd = (productsToAdd || []).map((p, i) => ({
            ...p,
            priceDelivery: p.priceDelivery || p.price,
            ...(isOverwrite && {
              overwriteValues: {
                type: OverwriteType.ADD,
                hidden: false,
                unavailable: false,
                title: "",
                priceValue: "",
                priceValueDelivery: "",
              },
            }),
            // Give it random sort key. Will be shuffled in MenuCategoryGrid component
            sortKey: i + 1,
            ...(isOverwrite && {
              originalSortKey: p.sortKey,
            }),
          }));
          setModalOpen(false);
          append(indexedProductsToAdd);
        }}
      />
    </div>
  );
};
